import React, { useState, useEffect } from 'react';
import { Lock, Unlock, ShoppingCart, Package, Check, Sparkles } from 'lucide-react';
import { checkEpisodeAccess, createChapterCheckout, createBundleCheckout, getComicBundleInfo } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

export function LockedEpisodeOverlay({ 
  episodeId, 
  comicId, 
  episodeTitle, 
  episodeNumber, 
  price = 1.50,
  onUnlock 
}) {
  const { isAuthenticated } = useAuth();
  const [access, setAccess] = useState(null);
  const [bundleInfo, setBundleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showBundleOption, setShowBundleOption] = useState(false);

  useEffect(() => {
    checkAccess();
    fetchBundleInfo();
  }, [episodeId]);

  const checkAccess = async () => {
    if (!isAuthenticated) {
      setAccess({ can_access: false, reason: 'not_logged_in' });
      setLoading(false);
      return;
    }

    try {
      const res = await checkEpisodeAccess(episodeId);
      setAccess(res.data);
      if (res.data.can_access && onUnlock) {
        onUnlock();
      }
    } catch (error) {
      setAccess({ can_access: false, reason: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBundleInfo = async () => {
    try {
      const res = await getComicBundleInfo(comicId);
      setBundleInfo(res.data);
    } catch (error) {
      console.error('Error fetching bundle info:', error);
    }
  };

  const handlePurchaseChapter = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      return;
    }

    try {
      setPurchasing(true);
      const res = await createChapterCheckout(episodeId, window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setPurchasing(false);
    }
  };

  const handlePurchaseBundle = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      return;
    }

    try {
      setPurchasing(true);
      const res = await createBundleCheckout(comicId, window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  // If user has access, don't show overlay
  if (access?.can_access) {
    return null;
  }

  return (
    <div 
      className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 flex items-center justify-center z-10"
      data-testid="locked-episode-overlay"
    >
      <div className="max-w-md w-full p-6 text-center">
        {/* Lock Icon */}
        <div className="w-20 h-20 rounded-full bg-[var(--primary)]/20 flex items-center justify-center mx-auto mb-6">
          <Lock size={40} className="text-[var(--primary)]" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-teko mb-2">Premium Episode</h2>
        <p className="text-[var(--text-secondary)] mb-6">
          Ep. {episodeNumber}: {episodeTitle}
        </p>

        {/* Not logged in */}
        {!isAuthenticated ? (
          <div className="space-y-4">
            <p className="text-[var(--text-secondary)]">
              Please login to purchase this episode
            </p>
            <Button onClick={() => window.location.href = '/login'} className="w-full">
              Login to Continue
            </Button>
          </div>
        ) : (
          <>
            {/* Purchase Options */}
            {!showBundleOption ? (
              <div className="space-y-4">
                {/* Single Chapter */}
                <Button
                  onClick={handlePurchaseChapter}
                  disabled={purchasing}
                  className="w-full bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                >
                  <ShoppingCart size={16} className="mr-2" />
                  Unlock This Chapter - ${price.toFixed(2)}
                </Button>

                {/* Bundle Option */}
                {bundleInfo?.has_premium_content && bundleInfo.premium_episode_count > 1 && (
                  <button
                    onClick={() => setShowBundleOption(true)}
                    className="w-full text-sm text-[var(--primary)] hover:underline"
                  >
                    Or unlock all {bundleInfo.premium_episode_count} premium episodes (save {bundleInfo.discount_percentage}%)
                  </button>
                )}

                {/* Subscription Alternative */}
                <div className="pt-4 border-t border-[var(--border)]">
                  <p className="text-xs text-[var(--text-secondary)] mb-2">
                    Or get unlimited access with a subscription
                  </p>
                  <a href="/pricing" className="text-[var(--primary)] text-sm hover:underline">
                    View subscription plans →
                  </a>
                </div>
              </div>
            ) : (
              /* Bundle Purchase View */
              <div className="space-y-4">
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Package size={20} className="text-[var(--primary)]" />
                    <span className="font-medium">Complete Bundle</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">Episodes included</span>
                      <span>{bundleInfo.premium_episode_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">Individual price</span>
                      <span className="line-through text-[var(--text-secondary)]">
                        ${bundleInfo.individual_total?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-500">
                      <span>You save</span>
                      <span>${bundleInfo.savings?.toFixed(2)} ({bundleInfo.discount_percentage}% off)</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-[var(--border)]">
                      <span>Bundle price</span>
                      <span className="text-[var(--primary)]">${bundleInfo.bundle_price?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handlePurchaseBundle}
                  disabled={purchasing}
                  className="w-full"
                >
                  <Package size={16} className="mr-2" />
                  Get Complete Bundle
                </Button>

                <button
                  onClick={() => setShowBundleOption(false)}
                  className="text-sm text-[var(--text-secondary)] hover:text-[var(--foreground)]"
                >
                  ← Just this chapter (${price.toFixed(2)})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Premium badge for episode list
export function PremiumBadge({ price }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full">
      <Lock size={10} />
      ${price?.toFixed(2) || '1.50'}
    </span>
  );
}

// Access badge showing how user unlocked content
export function AccessBadge({ reason }) {
  const badges = {
    free: null,
    subscription: (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
        <Sparkles size={10} />
        Subscriber Access
      </span>
    ),
    purchased: (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
        <Check size={10} />
        Purchased
      </span>
    ),
    bundle: (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
        <Package size={10} />
        Bundle
      </span>
    )
  };

  return badges[reason] || null;
}

export default LockedEpisodeOverlay;
